import { TrendingUp, Users, DollarSign } from "lucide-react";
import { Divider, Button } from "@jem-hr/jem-ui";

// ─── Types ───────────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
}

// ─── Data ────────────────────────────────────────────────────────────────────

const savingsData = {
  employeesSignedUp: 1247,
  totalSaved: 2450789.5,
  averageSavings: 1965.43,
};

// ─── Stat Card Component ─────────────────────────────────────────────────────

function StatCard({ title, value, icon, description, trend }: StatCardProps) {
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-lg hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-regular text-greyscale-text-caption mb-xxs">
            {title}
          </p>
          <h3 className="text-3xl font-semibold text-greyscale-text-title mb-xxxs">
            {value}
          </h3>
          {description && (
            <p className="text-xs text-greyscale-text-body">{description}</p>
          )}
          {trend && (
            <div className="flex items-center gap-xxxs mt-xs">
              <TrendingUp
                size={14}
                className={trend.isPositive ? "text-green-600" : "text-red-600"}
              />
              <span
                className={`text-xs font-semibold ${
                  trend.isPositive ? "text-green-600" : "text-red-600"
                }`}
              >
                {trend.value}
              </span>
              <span className="text-xs text-greyscale-text-caption">
                vs last month
              </span>
            </div>
          )}
        </div>
        <div className="rounded-full bg-secondary-pink-100 p-sm">{icon}</div>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────���────

export function Savings() {
  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Savings Overview
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Track employee savings participation, total savings, and average
            savings per employee.
          </p>
        </div>
        <Button>Export Report</Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-md mt-md">
        <StatCard
          title="Employees Signed Up"
          value={savingsData.employeesSignedUp.toLocaleString()}
          icon={<Users size={24} className="text-secondary-pink-700" />}
          description="Total employees enrolled in savings program"
          trend={{ value: "+12.3%", isPositive: true }}
        />

        <StatCard
          title="Total Savings"
          value={`R ${savingsData.totalSaved.toLocaleString("en-ZA", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          icon={<DollarSign size={24} className="text-secondary-pink-700" />}
          description="Cumulative savings across all employees"
          trend={{ value: "+8.7%", isPositive: true }}
        />

        <StatCard
          title="Average Savings"
          value={`R ${savingsData.averageSavings.toLocaleString("en-ZA", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          icon={<TrendingUp size={24} className="text-secondary-pink-700" />}
          description="Average savings per employee"
          trend={{ value: "-2.1%", isPositive: false }}
        />
      </div>

      <Divider variant="primary" className="my-lg" />

      {/* Additional Content Area */}
      <div>
        <h2 className="text-base font-semibold text-greyscale-text-title mb-md">
          Savings Breakdown
        </h2>
        <div className="rounded-lg border border-greyscale-border bg-neutral-50 p-lg">
          <p className="text-sm text-greyscale-text-caption">
            Detailed savings analytics and reports will be displayed here.
          </p>
        </div>
      </div>
    </div>
  );
}
